import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import type { AdminCommunication, NotificationChannel } from "../shared/communications-types";
import { addableChannelTypes } from "../shared/communication-selectors";
import { resolveCommunicationError } from "../shared/resolve-communication-error";
import { useCreateChannel } from "../catalog/use-communications";

interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly communication: AdminCommunication;
}

export function AddChannelDialog({ open, onOpenChange, communication }: Props) {
  const { t } = useTranslation();
  const create = useCreateChannel();

  // Types not already present (a soft-deleted type reappears here and the POST
  // revives it). Email-only comms only offer EMAIL.
  const options = useMemo(() => addableChannelTypes(communication), [communication]);

  const [channel, setChannel] = useState<NotificationChannel | "">("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setChannel(options[0] ?? "");
      setError(null);
    }
  }, [open, options]);

  const handleSubmit = async () => {
    if (channel === "" || create.isPending) return;
    setError(null);
    try {
      await create.mutateAsync({
        communicationId: communication.id,
        input: { channel },
      });
      onOpenChange(false);
    } catch (err) {
      setError(resolveCommunicationError(err, t));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !create.isPending && onOpenChange(next)}>
      <DialogContent data-testid="communication-add-channel-dialog">
        <DialogHeader>
          <DialogTitle>{t("communications.channel.add.title")}</DialogTitle>
          <DialogDescription>{t("communications.channel.add.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {options.length === 0 ? (
            <p
              data-testid="communication-add-channel-none"
              className="rounded-md border border-border bg-muted/30 p-2 text-sm text-muted-foreground"
            >
              {t("communications.channel.add.none")}
            </p>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="add-channel-type">
                {t("communications.channel.add.channelField")}
              </Label>
              <select
                id="add-channel-type"
                data-testid="communication-add-channel-input"
                value={channel}
                onChange={(e) => setChannel(e.target.value as NotificationChannel)}
                className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {options.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          )}

          {error && (
            <p data-testid="communication-add-channel-error" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            {t("communications.actions.cancel")}
          </Button>
          <Button
            data-testid="communication-add-channel-submit"
            onClick={handleSubmit}
            disabled={channel === "" || create.isPending}
          >
            {create.isPending
              ? t("communications.actions.saving")
              : t("communications.channel.add.button")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
